
import { SEARCH_EXACT_PATH } from '@marketplace/graphql/sections.js'
import { unlocalizeRouteName } from '@marketplace/helpers/router.js'
import { trackSearchEvents } from '@marketplace/helpers/segment/index.js'
import { toggleBodyScroll } from '@marketplace/mixins/body.js'
import axios from 'axios'
import debounce from 'lodash/debounce'
import { mapGetters } from 'vuex'

import SearchSuggestions from './search-suggestions.vue'

const MIN_SEARCH_QUERY_LENGTH = 2

export default {
    // this component handles the search functionality in the header
    name: 'HeaderCoreSearch',

    components: {
        SearchSuggestions,
    },

    props: {
        isDropshipping: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            searchQuery: '',
            generalSuggestions: [],
            storefrontSuggestions: [],
            showResults: false,
            isLoading: false,
            resultsCount: 0,
            hasBeenTracked: false,

            fetchAutocomplete: null,

            MIN_SEARCH_QUERY_LENGTH,
        }
    },

    computed: {
        ...mapGetters('global', ['preferredView']),
        ...mapGetters('feature', ['hasOneBrandFeature']),
        ...mapGetters('ds', ['dsStorefronts']),

        urlQuery() {
            return this.$route?.query?.search || ''
        },
        showResultsAndOverlay() {
            if (this.$breakpoint.includes('touch')) {
                return this.showResults
            }

            if (this.$route.query?.search && this.showResults) {
                return this.showResults
            }

            return (
                this.searchQuery.length >= MIN_SEARCH_QUERY_LENGTH &&
                this.showResults
            )
        },

        isMobile() {
            return this.$breakpoint.includes('mobile')
        },
    },

    watch: {
        // Reset the hasBeenTracked flag on route change as before
        $route(to) {
            this.hasBeenTracked = false
            if (!['search'].includes(unlocalizeRouteName(to.name))) {
                this.searchQuery = ''
            }
        },
    },

    created() {
        this.fetchAutocomplete = debounce(this.fetchAutocompleteFunction, 300)
    },

    mounted() {
        this.searchQuery = this.urlQuery
    },

    methods: {
        fetchAutocompleteFunction(event) {
            if (
                this.searchQuery.trim().length <= MIN_SEARCH_QUERY_LENGTH ||
                event.key === 'Escape'
            ) {
                return
            }

            const query = new URLSearchParams()

            query.set('query', encodeURIComponent(this.searchQuery))

            if (this.isDropshipping) {
                query.set('dropshipping', 'true')
            }

            this.isLoading = true

            axios({
                method: 'get',
                'content-type': 'application/json',
                accept: 'application/json',
                url: this.localeUrl(`/api/internal/autocomplete?${query}`),
            })
                .then((response) => {
                    this.showResults = true

                    this.generalSuggestions = (
                        response.data.general_search_suggestions || []
                    ).map((suggestion) => {
                        let url = suggestion.url
                        if (url.startsWith('/listings')) {
                            url = url.replace(
                                'listings',
                                this.selectedView(url)
                            )
                        }
                        return { ...suggestion, url }
                    })

                    let storeSuggestions =
                        response.data.storefront_search_suggestions || []

                    if (
                        this.isDropshipping &&
                        this.hasOneBrandFeature &&
                        Array.isArray(this.dsStorefronts) &&
                        this.dsStorefronts.length > 0
                    ) {
                        const myStoreNames = this.dsStorefronts.map((sf) =>
                            sf.storefront?.name?.toLowerCase()
                        )

                        storeSuggestions = storeSuggestions.filter((sugg) =>
                            myStoreNames.includes(
                                sugg.suggestion?.toLowerCase()
                            )
                        )
                    }

                    this.storefrontSuggestions = storeSuggestions

                    this.resultsCount =
                        this.generalSuggestions.length +
                        this.storefrontSuggestions.length

                    if (!this.hasBeenTracked) {
                        this.hasBeenTracked = true
                        trackSearchEvents('showSuggestion', {
                            query: this.searchQuery,
                        })
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        preventDefault($event) {
            $event.preventDefault()
        },
        closeSearchOnEscape(event) {
            if (event.key === 'Escape') {
                this.closeSearch()
            }
        },
        async submitForm(event) {
            event?.preventDefault()
            this.fetchAutocomplete.cancel() // cancel any pending requests

            if (
                this.searchQuery.trim().length <= MIN_SEARCH_QUERY_LENGTH &&
                !this.$route.query?.search
            ) {
                return
            }

            trackSearchEvents('goToSearch', { query: this.searchQuery })

            if (!this.searchQuery) {
                this.goToSearchPage('/search', { search: this.searchQuery })

                return
            }

            this.isLoading = true
            const exactPath = await this.getExactPath(this.searchQuery)

            if (exactPath) {
                this.goToSearchPage(exactPath, null)
            } else {
                this.goToSearchPage('/search', { search: this.searchQuery })
            }

            this.closeSearch()
        },
        async getExactPath(searchTerm) {
            let path = null

            if (
                this.isDropshipping ||
                searchTerm.trim().length <= MIN_SEARCH_QUERY_LENGTH
            ) {
                return path
            }

            await this.$apollo
                .query({
                    query: SEARCH_EXACT_PATH,
                    variables: {
                        searchTerm,
                    },
                })
                .then((res) => {
                    path = res.data.searchExactPath
                    if (path && path.startsWith('/listings')) {
                        path = path.replace('listings', this.selectedView(path))
                    }
                })

            return path
        },
        closeSearch(clearSearchQuery = false) {
            document.removeEventListener('keyup', this.closeSearchOnEscape)

            this.showResults = false
            this.isLoading = false
            this.resultsCount = 0

            if (clearSearchQuery) {
                this.searchQuery = ''
            }

            this.generalSuggestions = this.storefrontSuggestions = []
            this.$refs?.searchInput?.blur()

            if (this.isMobile) {
                toggleBodyScroll(false)
            }
        },
        onFocus() {
            if (this.isMobile) {
                toggleBodyScroll(true)
            }

            this.showResults = true

            document.addEventListener('keyup', this.closeSearchOnEscape)
        },
        onBlur: debounce(function () {
            if (this.isMobile) {
                return
            }

            this.closeSearch()
        }, 100),

        /**
         * When none of the autocomplete options are chosen you may submit the search query.
         * If it matches any existing category a direct link {path} without a query will be delivered.
         * If a non-vue page use default window.location to redirect
         *
         * @param {string} path
         * @param {object} query
         */
        goToSearchPage(path, query) {
            if (this.$page.currentRouteName) {
                if (this.isDropshipping) {
                    this.$router.push({
                        path: this.localeUrl(`ds${path}`),
                        query,
                    })
                } else {
                    this.$router.push({ path: this.localeUrl(path), query })
                }

                this.closeSearch()
            } else {
                const redirectPath = query
                    ? `${path}?search=${encodeURIComponent(this.searchQuery)}`
                    : path
                window.location.href = this.localeUrl(redirectPath)
            }
        },
        clear() {
            this.searchQuery = ''
            this.$refs.searchInput.focus()
        },
        hasTwoSlashes(url) {
            return (url.match(/\//g) || []).length === 2
        },
        selectedView(url) {
            if (!this.$helpers.isLoggedIn || url.match(/\//g)?.length === 2) {
                return 'marketplace'
            }

            if (this.preferredView) {
                return this.preferredView
            }

            if (this.$breakpoint.includes('touch')) {
                return 'wall'
            }
            return 'listings'
        },
    },
}
